import { forwardRef, useImperativeHandle } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2 } from 'lucide-react'
import type { z } from 'zod'

import { httpConfigSchema } from '@/schemas/workflow-node'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type HttpConfig = z.infer<typeof httpConfigSchema>

export interface NodeFormProps {
  defaultValues: Record<string, unknown>
  onSubmit: (data: Record<string, unknown>) => void
  disabled?: boolean
}

export interface NodeFormHandle {
  submit: () => void
}

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const
const AUTH_TYPES = ['none', 'bearer', 'basic'] as const
const BODY_METHODS: string[] = ['POST', 'PUT', 'PATCH']

export const HttpNodeForm = forwardRef<NodeFormHandle, NodeFormProps>(
  ({ defaultValues, onSubmit, disabled }, ref) => {
    const { t } = useTranslation('workflows')

    const {
      register,
      handleSubmit,
      control,
      watch,
      setValue,
      formState: { errors },
    } = useForm<HttpConfig>({
      resolver: zodResolver(httpConfigSchema),
      defaultValues: defaultValues as HttpConfig,
    })

    const { fields, append, remove } = useFieldArray({
      control,
      name: 'headers',
    })

    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit((data) => onSubmit(data as Record<string, unknown>))()
      },
    }))

    const method = watch('method')
    const authType = watch('auth_type')
    const showBody = BODY_METHODS.includes(method)
    const showAuthValue = authType !== 'none'

    return (
      <form
        className="space-y-4"
        onSubmit={handleSubmit((data) => onSubmit(data as Record<string, unknown>))}
      >
        {/* Method */}
        <div className="space-y-2">
          <Label htmlFor="method">Method</Label>
          <Select
            value={method}
            onValueChange={(value) => setValue('method', value as HttpConfig['method'])}
            disabled={disabled}
          >
            <SelectTrigger className="bg-white/5 border-gray-700/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HTTP_METHODS.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.method && (
            <p className="text-sm text-destructive">{t('flow.requiredField')}</p>
          )}
        </div>

        {/* URL */}
        <div className="space-y-2">
          <Label htmlFor="url">URL</Label>
          <Input
            id="url"
            className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
            placeholder="https://api.example.com/{{variable}}"
            disabled={disabled}
            {...register('url')}
          />
          {errors.url && (
            <p className="text-sm text-destructive">{t('flow.requiredField')}</p>
          )}
        </div>

        {/* Headers */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Headers</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1 border-gray-700/50 text-gray-300"
              onClick={() => append({ key: '', value: '' })}
              disabled={disabled || fields.length >= 50}
            >
              <Plus className="w-4 h-4" />
              Add header
            </Button>
          </div>
          {fields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2">
              <Input
                className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
                placeholder="Key"
                disabled={disabled}
                {...register(`headers.${index}.key`)}
              />
              <Input
                className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
                placeholder="Value"
                disabled={disabled}
                {...register(`headers.${index}.value`)}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0 text-gray-500 hover:text-red-400 shrink-0"
                onClick={() => remove(index)}
                disabled={disabled}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          {errors.headers && (
            <p className="text-sm text-destructive">{errors.headers.message}</p>
          )}
        </div>

        {/* Body (conditional) */}
        {showBody && (
          <div className="space-y-2">
            <Label htmlFor="body">Body</Label>
            <Textarea
              id="body"
              className="font-mono bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
              disabled={disabled}
              {...register('body')}
            />
            {errors.body && (
              <p className="text-sm text-destructive">{errors.body.message}</p>
            )}
          </div>
        )}

        {/* Auth Type */}
        <div className="space-y-2">
          <Label htmlFor="auth_type">Auth Type</Label>
          <Select
            value={authType}
            onValueChange={(value) => setValue('auth_type', value as HttpConfig['auth_type'])}
            disabled={disabled}
          >
            <SelectTrigger className="bg-white/5 border-gray-700/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AUTH_TYPES.map((a) => (
                <SelectItem key={a} value={a}>
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Auth Value (conditional) */}
        {showAuthValue && (
          <div className="space-y-2">
            <Label htmlFor="auth_value">Auth Value</Label>
            <Input
              id="auth_value"
              type="password"
              className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
              disabled={disabled}
              {...register('auth_value')}
            />
          </div>
        )}
      </form>
    )
  }
)

HttpNodeForm.displayName = 'HttpNodeForm'
